import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BookOpen, Award, Target, Users } from "lucide-react";
import { Separator } from "../components/ui/separator";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>About FutureThought | B2B Technology Intelligence</title>
        <meta name="description" content="Learn about FutureThought, our mission to deliver credible B2B technology intelligence, our editorial standards, and the experience behind our analysis." />
        <link rel="canonical" href="https://futurethought.emergent.host/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About FutureThought",
            "description": "B2B technology intelligence and thought leadership by Jit Singh",
            "mainEntity": {
              "@type": "Organization",
              "name": "FutureThought",
              "url": "https://futurethought.emergent.host",
              "founder": { "@type": "Person", "name": "Jit Singh" }
            }
          })}
        </script>
      </Helmet>

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold font-['Playfair_Display']">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="mb-16">
            <span className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4 block">About</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold tracking-tight mb-6" data-testid="about-title">
              FutureThought
            </h1>
            <p className="text-xl text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">
              Credible technology intelligence for professionals navigating the forces reshaping business, infrastructure, and society.
            </p>
          </div>

          {/* Mission */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Target size={20} className="text-[hsl(var(--primary))]" />
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold">Our Mission</h2>
            </div>
            <div className="text-[hsl(var(--foreground))] leading-relaxed space-y-4 text-justify">
              <p>FutureThought exists to bridge the gap between technical complexity and strategic clarity. We cover the technologies that matter most to business leaders, from artificial intelligence and semiconductor manufacturing to clean energy infrastructure and space systems, with the depth and rigor that informed decision-making requires.</p>
              <p>We believe that the best technology analysis is not about predicting the future but about understanding the structural forces that shape it: capital flows, supply chain dynamics, regulatory frameworks, and the engineering realities behind headline announcements. Our readers are professionals who need to separate signal from noise in a landscape where both move fast.</p>
              <p>Every article we publish is built on primary sources, verified data, and firsthand industry analysis. We do not aggregate press releases. We do not speculate without evidence. We write for the reader who needs to make decisions, not just stay informed.</p>
            </div>
          </section>

          <Separator className="mb-16 bg-[hsl(var(--border))]" />

          {/* About the Writer */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Users size={20} className="text-[hsl(var(--primary))]" />
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold">About the Writer</h2>
            </div>
            <div className="text-[hsl(var(--foreground))] leading-relaxed space-y-4 text-justify">
              <p>FutureThought is written and edited by <strong>Jit Singh</strong>, a technology analyst and writer with five years of experience in thought leadership research and communication. Jit's work focuses on the intersection of emerging technology, enterprise strategy, and industrial transformation.</p>
              <p>With a background spanning B2B technology analysis, content strategy, and market research, Jit brings a practitioner's perspective to complex technology topics. His writing has covered enterprise AI adoption, semiconductor supply chain dynamics, clean energy infrastructure, predictive maintenance systems, and the evolving economics of data center operations.</p>
              <p>Jit's approach combines technical depth with strategic framing, translating engineering developments and market data into actionable intelligence for business leaders, investors, and technology decision-makers.</p>
            </div>
          </section>

          <Separator className="mb-16 bg-[hsl(var(--border))]" />

          {/* Editorial Standards */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Award size={20} className="text-[hsl(var(--primary))]" />
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold">Editorial Standards</h2>
            </div>
            <div className="text-[hsl(var(--foreground))] leading-relaxed space-y-4 text-justify">
              <p>We hold our content to a clear set of editorial standards designed to ensure accuracy, credibility, and value for our readers.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="font-semibold mb-3 text-[hsl(var(--foreground))]">Primary Sources</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">Every claim is backed by verifiable sources: official company announcements, peer-reviewed research, government filings, or direct industry data. We cite our sources transparently so readers can verify independently.</p>
              </div>
              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="font-semibold mb-3 text-[hsl(var(--foreground))]">Original Analysis</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">We do not rewrite press releases or aggregate headlines. Each article provides original analysis that connects developments to broader strategic implications for technology professionals.</p>
              </div>
              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="font-semibold mb-3 text-[hsl(var(--foreground))]">Corrections Policy</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">If we make an error, we correct it promptly and transparently. Material corrections are noted at the top of the affected article with a clear description of what was changed and when.</p>
              </div>
              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="font-semibold mb-3 text-[hsl(var(--foreground))]">Independence</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">Our editorial content is independent of advertising relationships. Display advertising on this site does not influence what we write, how we evaluate technologies, or which companies we cover.</p>
              </div>
            </div>
          </section>

          <Separator className="mb-16 bg-[hsl(var(--border))]" />

          {/* What We Cover */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen size={20} className="text-[hsl(var(--primary))]" />
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold">What We Cover</h2>
            </div>
            <div className="text-[hsl(var(--foreground))] leading-relaxed space-y-4 text-justify">
              <p>FutureThought covers the technologies and market dynamics that are reshaping enterprise infrastructure, industrial operations, and strategic planning. Our core coverage areas include:</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              {[
                { label: "Artificial Intelligence", desc: "Enterprise AI, agentic systems, LLM economics, and AI infrastructure" },
                { label: "Semiconductors", desc: "Chip manufacturing, supply chain dynamics, and compute architecture" },
                { label: "Sustainability", desc: "Clean energy, energy efficiency, and environmental technology" },
                { label: "Energy Storage", desc: "Battery technology, grid storage, and energy infrastructure" },
                { label: "Robotics", desc: "Industrial automation, humanoid robotics, and autonomous systems" },
                { label: "Space", desc: "Space infrastructure, orbital services, and deep space technology" },
              ].map((topic) => (
                <div key={topic.label} className="border border-[hsl(var(--border))] p-4 bg-[hsl(var(--card))]">
                  <h3 className="font-semibold text-sm text-[hsl(var(--primary))] mb-1">{topic.label}</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{topic.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="border border-[hsl(var(--border))] p-8 bg-[hsl(var(--card))] text-center">
            <h3 className="text-xl font-['Playfair_Display'] font-semibold mb-3">Have a Question or Want to Work Together?</h3>
            <p className="text-[hsl(var(--muted-foreground))] mb-6">We welcome inquiries about content partnerships, freelance services, and editorial feedback.</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity text-sm font-medium"
              data-testid="about-contact-cta"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="text-xl font-bold font-['Playfair_Display']">
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/privacy-policy" className="hover:text-[hsl(var(--foreground))] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[hsl(var(--foreground))] transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-[hsl(var(--foreground))] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
